import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, ShieldCheck, Plus, CheckCircle2, AlertTriangle, Layers,
  Clock, Save, RefreshCw, Eye, HelpCircle, X, ChevronRight, Check,
  Sliders, CalendarDays, Ban, MapPin, Building2, AlertCircle
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const WEEKDAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

const SATURDAY_RULES = [
  { id: 'none', label: 'All Saturdays are Working Days' },
  { id: 'all', label: 'Every Saturday is a Weekly Off' },
  { id: 'alternate_2_4', label: '2nd & 4th Saturdays are Weekly Off' },
  { id: 'alternate_1_3_5', label: '1st, 3rd & 5th Saturdays are Weekly Off' },
  { id: 'specific_weeks', label: 'Specific Saturdays (Choose Weeks)' },
];

const COUNTING_MODES = [
  {
    id: 'all_calendar_days',
    label: 'All Calendar Days',
    description: 'Counts every day including weekly offs and holidays towards the limit.'
  },
  {
    id: 'working_days_only',
    label: 'Working Days Only',
    description: 'Excludes weekly offs and public/company holidays. Only working days consume the allowed days.'
  },
  {
    id: 'working_days_and_holidays',
    label: 'Working Days + Holidays',
    description: 'Excludes weekly offs, but holidays count towards the allowed days.'
  },
  {
    id: 'working_days_and_weekoffs',
    label: 'Working Days + Weekly Offs',
    description: 'Excludes holidays, but weekly offs count towards the allowed days.'
  }
];

const DEFAULT_MODULES = {
  regularization: {
    counting_mode: 'all_calendar_days',
    custom_limit_days: null,
    request_date_included: false,
    is_enabled: true
  },
  local_conveyance: {
    counting_mode: 'all_calendar_days',
    custom_limit_days: null,
    request_date_included: false,
    is_enabled: true
  },
  outstation_conveyance: {
    counting_mode: 'all_calendar_days',
    custom_limit_days: null,
    request_date_included: false,
    is_enabled: true
  }
};

export default function CalendarValidationPolicyManager({ selectedCompany }) {
  const [policies, setPolicies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSavePayload, setPendingSavePayload] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    policy_name: '',
    company_id: selectedCompany || 'all',
    branch_id: 'all',
    weekly_offs: ['Sunday'],
    saturday_rule: 'alternate_2_4',
    saturday_weeks: [2, 4],
    holiday_calendar_id: 'default',
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: '',
    is_active: true,
    modules: JSON.parse(JSON.stringify(DEFAULT_MODULES)),
    notes: ''
  });

  // Preview State
  const [previewModule, setPreviewModule] = useState('regularization');
  const [previewRefDate, setPreviewRefDate] = useState(new Date().toISOString().split('T')[0]);
  const [previewLimitDays, setPreviewLimitDays] = useState(3);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Fetch Policies
  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/calendar-policies${selectedCompany ? `?company_id=${selectedCompany}` : ''}`;
      const res = await axios.get(url, { withCredentials: true });
      setPolicies(res.data?.policies || []);
    } catch (err) {
      console.error('Failed to fetch calendar policies', err);
      toast.error('Failed to load calendar policies');
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  // Fetch Branches
  const fetchBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/masters/branches`, { withCredentials: true });
      setBranches(res.data?.branches || res.data || []);
    } catch (err) {
      // Fallback
      setBranches([]);
    }
  }, []);

  useEffect(() => {
    fetchPolicies();
    fetchBranches();
  }, [fetchPolicies, fetchBranches]);

  useEffect(() => {
    if (selectedCompany) {
      setFormData(prev => ({ ...prev, company_id: selectedCompany }));
    }
  }, [selectedCompany]);

  // Run calculation preview
  const runPreview = useCallback(async (overrides = {}) => {
    setPreviewLoading(true);
    try {
      const payload = {
        reference_date: previewRefDate,
        limit_days: previewLimitDays,
        counting_mode: overrides.counting_mode || formData.modules[previewModule]?.counting_mode || 'working_days_only',
        weekly_offs: overrides.weekly_offs || formData.weekly_offs,
        saturday_rule: overrides.saturday_rule || formData.saturday_rule,
        saturday_weeks: overrides.saturday_weeks || formData.saturday_weeks,
        company_id: formData.company_id,
        branch_id: formData.branch_id,
        request_date_included: formData.modules[previewModule]?.request_date_included || false
      };

      const res = await axios.post(`${API_URL}/calendar-policies/preview`, payload, { withCredentials: true });
      setPreviewData(res.data);
    } catch (err) {
      console.error('Preview calculation error', err);
    } finally {
      setPreviewLoading(false);
    }
  }, [previewRefDate, previewLimitDays, previewModule, formData]);

  // Re-run preview on relevant parameter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      runPreview();
    }, 250);
    return () => clearTimeout(timer);
  }, [runPreview]);

  // Open Form for Create
  const handleOpenCreate = () => {
    setEditingPolicy(null);
    setFormData({
      policy_name: 'Standard Working Day Calendar Policy',
      company_id: selectedCompany || 'all',
      branch_id: 'all',
      weekly_offs: ['Sunday'],
      saturday_rule: 'alternate_2_4',
      saturday_weeks: [2, 4],
      holiday_calendar_id: 'default',
      effective_from: new Date().toISOString().split('T')[0],
      effective_to: '',
      is_active: true,
      modules: JSON.parse(JSON.stringify(DEFAULT_MODULES)),
      notes: ''
    });
    setShowModal(true);
  };

  // Open Form for Edit
  const handleOpenEdit = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      policy_name: policy.policy_name || '',
      company_id: policy.company_id || selectedCompany || 'all',
      branch_id: policy.branch_id || 'all',
      weekly_offs: policy.weekly_offs || ['Sunday'],
      saturday_rule: policy.saturday_rule || 'none',
      saturday_weeks: policy.saturday_weeks || [2, 4],
      holiday_calendar_id: policy.holiday_calendar_id || 'default',
      effective_from: policy.effective_from || new Date().toISOString().split('T')[0],
      effective_to: policy.effective_to || '',
      is_active: policy.is_active !== false,
      modules: {
        regularization: {
          ...DEFAULT_MODULES.regularization,
          ...(policy.modules?.regularization || {}),
          custom_limit_days: (policy.modules?.regularization?.custom_limit_days != null && policy.modules?.regularization?.custom_limit_days !== '')
            ? Number(policy.modules.regularization.custom_limit_days)
            : null
        },
        local_conveyance: {
          ...DEFAULT_MODULES.local_conveyance,
          ...(policy.modules?.local_conveyance || {}),
          custom_limit_days: (policy.modules?.local_conveyance?.custom_limit_days != null && policy.modules?.local_conveyance?.custom_limit_days !== '')
            ? Number(policy.modules.local_conveyance.custom_limit_days)
            : null
        },
        outstation_conveyance: {
          ...DEFAULT_MODULES.outstation_conveyance,
          ...(policy.modules?.outstation_conveyance || {}),
          custom_limit_days: (policy.modules?.outstation_conveyance?.custom_limit_days != null && policy.modules?.outstation_conveyance?.custom_limit_days !== '')
            ? Number(policy.modules.outstation_conveyance.custom_limit_days)
            : null
        }
      },
      notes: policy.notes || ''
    });
    setShowModal(true);
  };

  // Submit Handler with Confirmation
  const handleSubmitClick = (e) => {
    e.preventDefault();
    if (!formData.policy_name.trim()) {
      toast.error('Policy Name is required');
      return;
    }

    // Validate and serialize custom_limit_days for each module
    const sanitizedModules = {};
    const moduleLabels = {
      regularization: 'Attendance Regularization',
      local_conveyance: 'Local Conveyance',
      outstation_conveyance: 'Outstation Conveyance'
    };

    for (const [modKey, modConfig] of Object.entries(formData.modules || {})) {
      const label = moduleLabels[modKey] || modKey;
      let limitVal = modConfig.custom_limit_days;

      if (limitVal === '' || limitVal === undefined || limitVal === null) {
        limitVal = null;
      } else {
        const num = Number(limitVal);
        if (isNaN(num) || !Number.isInteger(num)) {
          toast.error(`${label}: Override limit days must be a valid whole number.`);
          return;
        }
        if (num < 1) {
          toast.error(`${label}: Override limit days must be at least 1 day.`);
          return;
        }
        limitVal = num;
      }

      sanitizedModules[modKey] = {
        ...modConfig,
        custom_limit_days: limitVal
      };
    }

    const payload = {
      ...formData,
      company_id: formData.company_id || 'all',
      branch_id: formData.branch_id || 'all',
      modules: sanitizedModules
    };

    // If active policy is being updated or created, require confirmation
    if (payload.is_active) {
      setPendingSavePayload(payload);
      setShowConfirmModal(true);
    } else {
      executeSave(payload);
    }
  };

  const executeSave = async (payload) => {
    setShowConfirmModal(false);
    try {
      if (editingPolicy && (editingPolicy.id || editingPolicy._id)) {
        const pId = editingPolicy.id || editingPolicy._id;
        await axios.put(`${API_URL}/calendar-policies/${pId}`, payload, { withCredentials: true });
        toast.success('Calendar & Date Validation Policy updated successfully');
      } else {
        await axios.post(`${API_URL}/calendar-policies`, payload, { withCredentials: true });
        toast.success('Calendar & Date Validation Policy created successfully');
      }
      setShowModal(false);
      await fetchPolicies();
    } catch (err) {
      console.error('Failed to save policy', err);
      const detail = err.response?.data?.detail;
      let displayMsg = 'Failed to save policy';
      if (typeof detail === 'string') {
        displayMsg = detail;
      } else if (Array.isArray(detail)) {
        displayMsg = detail.map(d => d.msg || (d.loc ? `${d.loc.slice(-1)[0]}: ${d.msg}` : JSON.stringify(d))).join(', ');
      } else if (err.message) {
        displayMsg = err.message;
      }
      toast.error(displayMsg);
    }
  };

  // Toggle Policy Active Status
  const handleToggleStatus = async (policy) => {
    const pId = policy.id || policy._id;
    try {
      await axios.put(`${API_URL}/calendar-policies/${pId}`, { is_active: !policy.is_active }, { withCredentials: true });
      toast.success(`Policy ${policy.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchPolicies();
    } catch (err) {
      toast.error('Failed to change policy status');
    }
  };

  // Toggle Weekday in Weekly Offs
  const toggleWeekday = (day) => {
    setFormData(prev => {
      const current = prev.weekly_offs || [];
      const updated = current.includes(day)
        ? current.filter(d => d !== day)
        : [...current, day];
      return { ...prev, weekly_offs: updated };
    });
  };

  // Toggle Saturday Specific Week
  const toggleSaturdayWeek = (weekNum) => {
    setFormData(prev => {
      const current = prev.saturday_weeks || [];
      const updated = current.includes(weekNum)
        ? current.filter(w => w !== weekNum)
        : [...current, weekNum].sort();
      return { ...prev, saturday_weeks: updated };
    });
  };

  // Update Module Rule
  const updateModuleField = (modKey, field, val) => {
    setFormData(prev => ({
      ...prev,
      modules: {
        ...prev.modules,
        [modKey]: {
          ...prev.modules[modKey],
          [field]: val
        }
      }
    }));
  };

  return (
    <div data-testid="calendar-validation-policy-manager" className="space-y-6">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-indigo-900 via-indigo-800 to-slate-900 rounded-2xl p-6 text-white shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/30 text-indigo-200 border border-indigo-400/30 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-indigo-300" /> Company Calendar Policy
            </span>
            <span className="text-xs text-indigo-200/80">Attendance Regularization • Local Conveyance • Travel</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Calendar &amp; Date Validation Policy</h2>
          <p className="text-indigo-200/90 text-xs sm:text-sm mt-1 max-w-2xl leading-relaxed">
            Configure company-wise weekly off days, Saturday working rules, holiday mapping, and specify whether weekly offs and holidays count toward the submission date limits for each module.
          </p>
        </div>
        <button
          data-testid="create-calendar-policy-btn"
          onClick={handleOpenCreate}
          className="px-4 py-2.5 bg-indigo-500 hover:bg-indigo-400 text-white rounded-xl text-sm font-semibold transition-all shadow-md hover:shadow-indigo-500/25 flex items-center gap-2 shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>New Calendar Policy</span>
        </button>
      </div>

      {/* Policies List Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-slate-800 text-sm sm:text-base">Configured Calendar Policies</h3>
            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full font-medium">{policies.length}</span>
          </div>
          <button
            onClick={fetchPolicies}
            className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {policies.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Calendar className="w-7 h-7" />
            </div>
            <p className="text-base font-semibold text-slate-700">No Custom Calendar Policy Configured</p>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              System is currently operating on the Default Calendar Policy (Sundays Off, Standard Calendar Days). Click below to create a custom policy.
            </p>
            <button
              onClick={handleOpenCreate}
              className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs sm:text-sm font-medium transition-all"
            >
              Configure First Calendar Policy
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs sm:text-sm">
              <thead className="bg-slate-50 text-slate-600 border-b border-slate-200/80 font-medium">
                <tr>
                  <th className="px-4 py-3">Policy Name</th>
                  <th className="px-4 py-3">Scope</th>
                  <th className="px-4 py-3">Weekly Offs</th>
                  <th className="px-4 py-3">Saturday Rule</th>
                  <th className="px-4 py-3">Regularization</th>
                  <th className="px-4 py-3">Local Conveyance</th>
                  <th className="px-4 py-3">Outstation</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {policies.map((p) => {
                  const regMode = p.modules?.regularization?.counting_mode || 'all_calendar_days';
                  const lcMode = p.modules?.local_conveyance?.counting_mode || 'all_calendar_days';
                  const ocMode = p.modules?.outstation_conveyance?.counting_mode || 'all_calendar_days';

                  return (
                    <tr key={p.id || p._id} data-testid={`policy-row-${p.id || p._id}`} className="hover:bg-slate-50/70 transition-colors">
                      <td className="px-4 py-3.5">
                        <div className="font-semibold text-slate-900">{p.policy_name}</div>
                        <div className="text-xs text-slate-400">Effective: {p.effective_from || 'Always'}</div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="inline-flex items-center gap-1 text-xs text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md font-medium">
                          <Building2 className="w-3 h-3 text-slate-500" />
                          {p.branch_id && p.branch_id !== 'all' ? `Branch: ${p.branch_id}` : 'All Branches'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex flex-wrap gap-1">
                          {(p.weekly_offs || ['Sunday']).map((w) => (
                            <span key={w} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200/60 rounded text-[11px] font-semibold">
                              {w.slice(0, 3)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-xs text-slate-600 font-medium">
                        {p.saturday_rule === 'alternate_2_4' ? '2nd & 4th Off' :
                         p.saturday_rule === 'alternate_1_3_5' ? '1st, 3rd, 5th Off' :
                         p.saturday_rule === 'all' ? 'All Off' : 'Working Day'}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                          regMode === 'working_days_only' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          regMode === 'all_calendar_days' ? 'bg-slate-100 text-slate-700' : 'bg-blue-50 text-blue-700'
                        }`}>
                          {regMode.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                          lcMode === 'working_days_only' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          lcMode === 'all_calendar_days' ? 'bg-slate-100 text-slate-700' : 'bg-blue-50 text-blue-700'
                        }`}>
                          {lcMode.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                          ocMode === 'working_days_only' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          ocMode === 'all_calendar_days' ? 'bg-slate-100 text-slate-700' : 'bg-blue-50 text-blue-700'
                        }`}>
                          {ocMode.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <button
                          onClick={() => handleToggleStatus(p)}
                          className={`px-2 py-0.5 rounded-full text-[11px] font-semibold transition-colors ${
                            p.is_active ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                        >
                          {p.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-3.5 text-right space-x-2">
                        <button
                          data-testid={`edit-policy-btn-${p.id || p._id}`}
                          onClick={() => handleOpenEdit(p)}
                          className="text-xs font-semibold text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-lg transition-colors"
                        >
                          Configure
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE / EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-5 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in duration-200">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-indigo-500/20 rounded-xl border border-indigo-400/30 text-indigo-300">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base sm:text-lg">
                    {editingPolicy ? 'Configure Calendar & Date Validation Policy' : 'New Calendar & Date Validation Policy'}
                  </h3>
                  <p className="text-xs text-indigo-200/80">Company-specific week-off patterns, holiday rules, and module date validation</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-6">
              {/* Section 1: Basic Scope & Name */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                    Policy Name *
                  </label>
                  <input
                    data-testid="policy-name-input"
                    type="text"
                    value={formData.policy_name}
                    onChange={(e) => setFormData({ ...formData, policy_name: e.target.value })}
                    placeholder="e.g. Corporate Calendar Policy"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                    Applicable Branch
                  </label>
                  <select
                    value={formData.branch_id}
                    onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none"
                  >
                    <option value="all">All Branches (Company-Wide)</option>
                    {branches.map(b => (
                      <option key={b.id || b._id} value={b.id || b._id}>{b.name || b.branch_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                    Holiday Calendar
                  </label>
                  <select
                    value={formData.holiday_calendar_id}
                    onChange={(e) => setFormData({ ...formData, holiday_calendar_id: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none"
                  >
                    <option value="default">Mapped Company Holiday Calendar (Default)</option>
                    <option value="all">All Active Public &amp; Company Holidays</option>
                  </select>
                </div>
              </div>

              {/* Section 2: Weekly Off Days Selection */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-bold text-slate-800">1. Weekly Off Days</h4>
                    <p className="text-xs text-slate-500">Select which weekdays are considered weekly offs for this policy</p>
                  </div>
                  <span className="text-xs font-semibold text-indigo-700 bg-indigo-100 px-2.5 py-0.5 rounded-full">
                    {formData.weekly_offs.length} Day(s) Selected
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                  {WEEKDAYS.map((day) => {
                    const isSelected = formData.weekly_offs.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        data-testid={`weekoff-btn-${day}`}
                        onClick={() => toggleWeekday(day)}
                        className={`p-2.5 rounded-xl border text-xs font-semibold flex flex-col items-center justify-center gap-1 transition-all ${
                          isSelected
                            ? 'bg-indigo-600 text-white border-indigo-700 shadow-sm ring-2 ring-indigo-500/30'
                            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100 hover:border-slate-300'
                        }`}
                      >
                        <span className="text-sm">{day.slice(0, 3)}</span>
                        <span className={`text-[10px] ${isSelected ? 'text-indigo-200' : 'text-slate-400'}`}>
                          {isSelected ? 'Weekly Off' : 'Working'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Section 3: Saturday Rules Configuration */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80 space-y-3">
                <div>
                  <h4 className="text-sm font-bold text-slate-800">2. Saturday Working Pattern</h4>
                  <p className="text-xs text-slate-500">Configure how Saturdays are treated in this company calendar</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                  {SATURDAY_RULES.map((r) => {
                    const isSel = formData.saturday_rule === r.id;
                    return (
                      <button
                        key={r.id}
                        type="button"
                        data-testid={`sat-rule-${r.id}`}
                        onClick={() => setFormData({ ...formData, saturday_rule: r.id })}
                        className={`p-3 rounded-xl border text-left text-xs font-medium transition-all flex items-start gap-2.5 ${
                          isSel
                            ? 'bg-indigo-50 border-indigo-500 text-indigo-900 ring-1 ring-indigo-500'
                            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded-full mt-0.5 border flex items-center justify-center shrink-0 ${
                          isSel ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300 bg-white'
                        }`}>
                          {isSel && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-800">{r.label}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Sub-selector if specific_weeks chosen */}
                {formData.saturday_rule === 'specific_weeks' && (
                  <div className="pt-2 border-t border-slate-200/70 flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-slate-600">Select Off Saturdays:</span>
                    {[1, 2, 3, 4, 5].map((w) => {
                      const isSel = (formData.saturday_weeks || []).includes(w);
                      return (
                        <button
                          key={w}
                          type="button"
                          onClick={() => toggleSaturdayWeek(w)}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${
                            isSel ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-slate-600 border-slate-200'
                          }`}
                        >
                          {w}{w === 1 ? 'st' : w === 2 ? 'nd' : w === 3 ? 'rd' : 'th'} Sat
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Section 4: Module-Wise Date Counting Rules */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-bold text-slate-800">3. Module-Wise Date Validation Rules</h4>
                  <p className="text-xs text-slate-500">Configure whether weekly offs and holidays count toward the submission date limits</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Module 1: Regularization */}
                  <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-indigo-600" /> Attendance Regularization
                      </span>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">
                        Counting Mode
                      </label>
                      <select
                        data-testid="regularization-counting-mode"
                        value={formData.modules.regularization.counting_mode}
                        onChange={(e) => updateModuleField('regularization', 'counting_mode', e.target.value)}
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg bg-white outline-none focus:border-indigo-600 font-medium"
                      >
                        {COUNTING_MODES.map((m) => (
                          <option key={m.id} value={m.id}>{m.label}</option>
                        ))}
                      </select>
                      <p className="text-[11px] text-slate-500 mt-1">
                        {COUNTING_MODES.find(m => m.id === formData.modules.regularization.counting_mode)?.description}
                      </p>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">
                        Override Limit Days (Optional)
                      </label>
                      <input
                        data-testid="regularization-limit-input"
                        type="number"
                        min="1"
                        max="90"
                        value={formData.modules.regularization.custom_limit_days ?? ''}
                        onChange={(e) => {
                          const val = e.target.value.trim();
                          updateModuleField('regularization', 'custom_limit_days', val === '' ? null : val);
                        }}
                        placeholder="Default from Policy Master"
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg outline-none focus:border-indigo-600"
                      />
                    </div>
                  </div>

                  {/* Module 2: Local Conveyance */}
                  <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
                        <MapPin className="w-4 h-4 text-emerald-600" /> Local Conveyance
                      </span>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">
                        Counting Mode
                      </label>
                      <select
                        data-testid="local-conveyance-counting-mode"
                        value={formData.modules.local_conveyance.counting_mode}
                        onChange={(e) => updateModuleField('local_conveyance', 'counting_mode', e.target.value)}
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg bg-white outline-none focus:border-emerald-600 font-medium"
                      >
                        {COUNTING_MODES.map((m) => (
                          <option key={m.id} value={m.id}>{m.label}</option>
                        ))}
                      </select>
                      <p className="text-[11px] text-slate-500 mt-1">
                        {COUNTING_MODES.find(m => m.id === formData.modules.local_conveyance.counting_mode)?.description}
                      </p>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">
                        Override Limit Days (Optional)
                      </label>
                      <input
                        data-testid="local-conveyance-limit-input"
                        type="number"
                        min="1"
                        max="90"
                        value={formData.modules.local_conveyance.custom_limit_days ?? ''}
                        onChange={(e) => {
                          const val = e.target.value.trim();
                          updateModuleField('local_conveyance', 'custom_limit_days', val === '' ? null : val);
                        }}
                        placeholder="Default from Local Travel Policy"
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg outline-none focus:border-emerald-600"
                      />
                    </div>
                  </div>

                  {/* Module 3: Outstation Conveyance */}
                  <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
                        <Layers className="w-4 h-4 text-blue-600" /> Outstation Conveyance
                      </span>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">
                        Counting Mode
                      </label>
                      <select
                        data-testid="outstation-conveyance-counting-mode"
                        value={formData.modules.outstation_conveyance.counting_mode}
                        onChange={(e) => updateModuleField('outstation_conveyance', 'counting_mode', e.target.value)}
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg bg-white outline-none focus:border-blue-600 font-medium"
                      >
                        {COUNTING_MODES.map((m) => (
                          <option key={m.id} value={m.id}>{m.label}</option>
                        ))}
                      </select>
                      <p className="text-[11px] text-slate-500 mt-1">
                        {COUNTING_MODES.find(m => m.id === formData.modules.outstation_conveyance.counting_mode)?.description}
                      </p>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">
                        Override Limit Days (Optional)
                      </label>
                      <input
                        data-testid="outstation-conveyance-limit-input"
                        type="number"
                        min="1"
                        max="180"
                        value={formData.modules.outstation_conveyance.custom_limit_days ?? ''}
                        onChange={(e) => {
                          const val = e.target.value.trim();
                          updateModuleField('outstation_conveyance', 'custom_limit_days', val === '' ? null : val);
                        }}
                        placeholder="Default from Travel Policy"
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg outline-none focus:border-blue-600"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 5: Live Interactive Calculation Preview */}
              <div className="bg-slate-900 rounded-2xl p-5 text-white shadow-inner space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-800 pb-3">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-indigo-400" />
                    <span className="font-bold text-sm">Live Date Window Simulator</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-300">Preview</span>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs text-slate-400">Preview Module:</label>
                    <select
                      value={previewModule}
                      onChange={(e) => setPreviewModule(e.target.value)}
                      className="bg-slate-800 border border-slate-700 text-white rounded-lg text-xs px-2 py-1 outline-none"
                    >
                      <option value="regularization">Attendance Regularization</option>
                      <option value="local_conveyance">Local Conveyance</option>
                      <option value="outstation_conveyance">Outstation Conveyance</option>
                    </select>

                    <label className="text-xs text-slate-400 ml-2">Limit Days:</label>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={previewLimitDays}
                      onChange={(e) => setPreviewLimitDays(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-14 bg-slate-800 border border-slate-700 text-white rounded-lg text-xs px-2 py-1 outline-none text-center"
                    />

                    <label className="text-xs text-slate-400 ml-2">Reference Date:</label>
                    <input
                      type="date"
                      value={previewRefDate}
                      onChange={(e) => setPreviewRefDate(e.target.value)}
                      className="bg-slate-800 border border-slate-700 text-white rounded-lg text-xs px-2 py-1 outline-none"
                    />
                  </div>
                </div>

                {previewLoading ? (
                  <div className="py-6 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin text-indigo-400" /> Calculating window...
                  </div>
                ) : previewData ? (
                  <div className="space-y-4">
                    {/* Summary Metrics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-3">
                        <div className="text-[11px] text-slate-400 font-medium">Earliest Allowed Date</div>
                        <div className="text-base sm:text-lg font-bold text-emerald-400 mt-0.5">
                          {previewData.earliest_date}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          {previewData.total_calendar_days_spanned} calendar days back
                        </div>
                      </div>

                      <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-3">
                        <div className="text-[11px] text-slate-400 font-medium">Validation Days Counted</div>
                        <div className="text-base sm:text-lg font-bold text-indigo-300 mt-0.5">
                          {previewData.counted_dates_count} / {previewData.limit_days}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">Days consuming limit</div>
                      </div>

                      <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-3">
                        <div className="text-[11px] text-slate-400 font-medium">Weekly Offs Excluded</div>
                        <div className="text-base sm:text-lg font-bold text-purple-400 mt-0.5">
                          {previewData.excluded_weekoff_count}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">Did not consume limit</div>
                      </div>

                      <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-3">
                        <div className="text-[11px] text-slate-400 font-medium">Holidays Excluded</div>
                        <div className="text-base sm:text-lg font-bold text-amber-400 mt-0.5">
                          {previewData.excluded_holiday_count}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">Public &amp; mapped holidays</div>
                      </div>
                    </div>

                    {/* Day-by-Day Breakdown Grid */}
                    <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50 max-h-48 overflow-y-auto">
                      <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                        Window Date Breakdown (From Earliest Allowed to Reference Date)
                      </div>
                      <div className="space-y-1.5">
                        {(previewData.days_breakdown || []).map((day) => (
                          <div
                            key={day.date}
                            className={`flex items-center justify-between px-3 py-1.5 rounded-lg text-xs ${
                              day.classification === 'counted' ? 'bg-indigo-900/40 border border-indigo-700/40 text-indigo-200' :
                              day.classification === 'weekly_off' ? 'bg-purple-950/40 border border-purple-800/40 text-purple-300' :
                              day.classification === 'holiday' ? 'bg-amber-950/40 border border-amber-800/40 text-amber-300' :
                              'bg-slate-800 text-slate-300'
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <span className="font-mono font-semibold">{day.date}</span>
                              <span className="text-slate-400">({day.day_name})</span>
                              {day.holiday_name && (
                                <span className="text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.2 rounded border border-amber-500/30">
                                  {day.holiday_name}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {day.is_counted ? (
                                <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                  Counted Toward Limit
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-700 text-slate-300">
                                  {day.classification === 'weekly_off' ? 'Weekly Off (Excluded)' :
                                   day.classification === 'holiday' ? 'Holiday (Excluded)' :
                                   day.classification === 'holiday_on_weekoff' ? 'Holiday on Week-Off (Excluded Once)' : 'Excluded'}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Status and Notes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                    Effective From
                  </label>
                  <input
                    type="date"
                    value={formData.effective_from || ''}
                    onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl outline-none focus:border-indigo-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                    Policy Status
                  </label>
                  <select
                    value={formData.is_active ? 'active' : 'inactive'}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl bg-white outline-none focus:border-indigo-600 font-medium"
                  >
                    <option value="active">Active (Policy in Effect)</option>
                    <option value="inactive">Inactive / Draft</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="save-calendar-policy-btn"
                onClick={handleSubmitClick}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold transition-all shadow-md flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Calendar Policy</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRMATION MODAL BEFORE SAVING ACTIVE POLICY */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full p-6 space-y-4 animate-in zoom-in-95 duration-150">
            <div className="w-12 h-12 bg-amber-100 text-amber-700 rounded-2xl flex items-center justify-center mx-auto">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="text-center">
              <h4 className="text-lg font-bold text-slate-900">Confirm Calendar Policy Change</h4>
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                You are about to activate or update <strong>{pendingSavePayload?.policy_name}</strong>.
                This will govern the calculation of allowed submission dates for future requests and pending edits across Regularization, Local Conveyance, and Outstation Conveyance.
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 text-xs space-y-1.5 text-slate-700">
              <div className="flex justify-between">
                <span className="text-slate-500">Weekly Offs:</span>
                <span className="font-semibold">{pendingSavePayload?.weekly_offs?.join(', ')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Regularization Mode:</span>
                <span className="font-semibold">{pendingSavePayload?.modules?.regularization?.counting_mode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Local Conveyance Mode:</span>
                <span className="font-semibold">{pendingSavePayload?.modules?.local_conveyance?.counting_mode}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-2.5 border border-slate-300 text-slate-700 rounded-xl text-xs font-semibold hover:bg-slate-100 transition-colors"
              >
                Go Back &amp; Review
              </button>
              <button
                type="button"
                data-testid="confirm-save-policy-btn"
                onClick={() => executeSave(pendingSavePayload)}
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold transition-all shadow-md"
              >
                Confirm &amp; Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
